import { useState } from 'react'
import type { OrderStatus, RiskLevel } from '@/api/types'
import { useUser } from '@/auth/useAuth'
import { can } from '@/auth/rbac'
import { orderStatusLabel, riskLabel } from '@/i18n/labels'
import { Button, EmptyState, ErrorState, LoadingState, Select } from '@/ui'
import { OrderCard } from './OrderCard'
import { OrderCreateSheet } from './OrderCreateSheet'
import { useOrders, type OrderFilters } from './queries'

const STATUSES: OrderStatus[] = [
  'DRAFT',
  'PLANNED',
  'IN_PRODUCTION',
  'PAUSED',
  'READY',
  'SHIPPED',
  'CANCELLED',
]
const RISKS: RiskLevel[] = ['GREEN', 'YELLOW', 'ORANGE', 'RED']

export function OrdersListPage() {
  const user = useUser()
  const [filters, setFilters] = useState<OrderFilters>({})
  const [createOpen, setCreateOpen] = useState(false)
  const { data, isLoading, isError, error, refetch } = useOrders(filters)
  const canCreate = can(user.role, 'orders.manage')

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold text-tg-text">Заказы</h1>
        {canCreate && (
          <Button size="md" onClick={() => setCreateOpen(true)}>
            + Заказ
          </Button>
        )}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <Select
          value={filters.status ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, status: (e.target.value || undefined) as OrderStatus | undefined }))
          }
        >
          <option value="">Все статусы</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {orderStatusLabel[s]}
            </option>
          ))}
        </Select>
        <Select
          value={filters.risk ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, risk: (e.target.value || undefined) as RiskLevel | undefined }))
          }
        >
          <option value="">Любой риск</option>
          {RISKS.map((r) => (
            <option key={r} value={r}>
              {riskLabel[r]}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="Заказов нет"
          hint={canCreate ? 'Создайте первый заказ.' : 'Заказы появятся здесь.'}
          action={
            canCreate ? <Button onClick={() => setCreateOpen(true)}>Создать заказ</Button> : undefined
          }
        />
      ) : (
        <div>
          {data.items.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}

      <OrderCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
